import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { MessageCircle } from "lucide-react";
import { useStore } from "@/context/StoreContext";

const TITLES = {
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  shipping: "Shipping Policy",
  returns: "Returns & Exchange Policy",
  buyback: "Lifetime Buyback Policy",
  exchange: "Gold & Silver Exchange",
  contact: "Contact Us",
};

export default function Policies() {
  const { slug } = useParams();
  const { config } = useStore();
  const legal = config?.legal || {};
  const support = config?.support || {};
  const title = TITLES[slug] || "Policy";

  useEffect(() => { document.title = `${title} | Siyaara`; }, [title]);

  return (
    <div className="container-x py-12 max-w-3xl" data-testid={`policy-${slug}`}>
      <p className="eyebrow !text-gold">SIYAARA POLICIES</p>
      <h1 className="font-heading text-3xl sm:text-4xl font-semibold mt-1">{title}</h1>
      <div className="hairline" />
      <div className="prose-sm text-ink-2 space-y-5 leading-relaxed mt-4 text-sm">

        {slug === "privacy" && (
          <>
            <p>We collect only what an order needs: your name, contact details, delivery address and order history. Payment details are handled entirely by our PCI-DSS compliant payment partner — we never see or store card numbers.</p>
            <p>Your data is used to fulfil orders, provide support and — only with your consent — send offers. We do not sell or rent personal data. You may request a copy or deletion of your data at any time by writing to us.</p>
            <p>Cookies are used for cart, session and analytics basics. Analytics are aggregated and not used to identify you personally.</p>
          </>
        )}

        {slug === "terms" && (
          <>
            <p>By purchasing from Siyaara you agree to these terms. Gold product prices are computed from the live metal rate plus making charges and GST, and are locked for the window shown in your cart; silver jewellery sells at a fixed MRP. Orders are confirmed on successful payment — Siyaara is prepaid-only (UPI, cards, netbanking, wallets, EMI).</p>
            <p>Weights and purity are as stated on each product page and invoice; gold jewellery is BIS hallmarked where applicable and coins are assay-certified with a verifiable serial. Personalised and engraved pieces are made to order and cannot be returned.</p>
            <p>Metal rates move with the market; the price you pay is the price locked at checkout, and your invoice records the rate used. Misuse of the verification system or fraudulent returns may lead to order cancellation and account closure.</p>
          </>
        )}

        {slug === "shipping" && (
          <>
            <p>We ship across India. In-stock pieces dispatch in 1–2 working days and arrive in 4–6 working days; made-to-order and personalised pieces take 7–10 working days before dispatch. Shipping is free above the threshold shown at checkout.</p>
            <p>Every parcel is fully insured and tamper-evident. High-value orders may require an OTP or ID check at delivery. Siyaara is prepaid-only — pay securely by UPI, card, netbanking, wallet or EMI at checkout.</p>
          </>
        )}

        {slug === "returns" && (
          <>
            <p>Unworn jewellery in original, untampered packaging with certificates intact can be returned within 2 days of delivery, and exchanged within 10 days. Personalised, engraved and resized pieces are made to order and non-returnable. Coins are returnable only with the tamper-proof seal unbroken.</p>
            <p>To start a return, write to us with your order number. Refunds are processed to the original payment method within 5–7 working days of the piece passing quality check.</p>
          </>
        )}

        {slug === "buyback" && (
          <>
            <p>Every Siyaara piece carries lifetime buyback. We buy back at the prevailing metal rate for the piece's purity and net weight on the day of buyback, less any applicable refining adjustment, after verification of the piece, its certificate and invoice.</p>
            <p>Buyback value is always calculated on metal weight — never on making charges, stones or fees. The exact computation is shared with you in writing before you confirm.</p>
          </>
        )}

        {slug === "exchange" && (
          <>
            <p>Exchange any Siyaara piece for new jewellery or coins at any time. Your old piece is valued at the day's rate for its purity and net weight, and the full value is adjusted against your new purchase.</p>
            <p>We also accept non-Siyaara gold and silver for exchange after purity testing in front of you or via a recorded assay — the test result and valuation are shared transparently before you decide.</p>
          </>
        )}

        {slug === "contact" && (
          <>
            <p>We answer within one working day — usually much faster during support hours.</p>
            <div className="bg-white border border-brand/10 rounded-xl p-5 space-y-2" data-testid="contact-details">
              {support.email && <p>Email: <a className="text-brand underline" href={`mailto:${support.email}`}>{support.email}</a></p>}
              {support.phone && <p>Phone: <a className="text-brand underline" href={`tel:${support.phone}`}>{support.phone}</a></p>}
              {support.whatsapp && (
                <p className="flex items-center gap-2">
                  <MessageCircle size={15} className="text-[#2E7D5B]" />
                  <a className="text-brand underline" target="_blank" rel="noreferrer" href={`https://wa.me/${support.whatsapp.replace(/\D/g, "")}`} data-testid="contact-whatsapp">Chat on WhatsApp</a>
                </p>
              )}
              {support.hours && <p>Support hours: {support.hours}</p>}
              {!support.email && !support.phone && !support.whatsapp && (
                <p className="text-ink-2 italic" data-testid="contact-empty">Contact details are being added — check back shortly.</p>
              )}
            </div>
          </>
        )}

        {!TITLES[slug] && <p>This policy page doesn't exist. <Link to="/" className="text-brand underline">Go home</Link>.</p>}

        {(legal.legal_name || legal.cin || legal.bis_reg || legal.address) && (
          <div className="border-t border-brand/10 pt-4 mt-6 text-xs font-mono space-y-1" data-testid="policy-legal">
            {legal.legal_name && <p>{legal.legal_name}</p>}
            {legal.cin && <p>CIN: {legal.cin}</p>}
            {legal.bis_reg && <p>BIS Registration: {legal.bis_reg}</p>}
            {legal.address && <p>Registered address: {legal.address}</p>}
            {legal.grievance_officer && <p>Grievance officer: {legal.grievance_officer}</p>}
          </div>
        )}
      </div>
    </div>
  );
}
